import React, { useCallback, useEffect, useState } from 'react';
import CareerChampWordmark from '../Brand/CareerChampWordmark';
import WorkspaceLoader from '../Onboarding/WorkspaceLoader';
import { useDispatch, useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';

import api from '../../api/axios';
import OnboardingFlow from '../Onboarding/OnboardingFlow';
import TutorialWizard from '../Onboarding/TutorialWizard';
import { clearFirstLoginFlag, fetchUserProfile } from '../../redux/AuthSlice/authSlice';
import { fetchATSJobsByPreference } from '../../redux/JobSlice/jobSlice';
import { fetchNewHiringSignals } from '../../redux/HSignalSlice/hsignalSlice';
import { uploadResume } from '../../redux/ResumeSlice/resumeSlice';
import {
    isAiProfileValid
} from '../../services/onboardingService';

const COUNTRY_OPTIONS = [
    { flag: "🇺🇸", name: "United States" },
    { flag: "🇬🇧", name: "United Kingdom" },
    { flag: "🇨🇦", name: "Canada" },
    { flag: "🇦🇺", name: "Australia" },
    { flag: "🇩🇪", name: "Germany" },
    { flag: "🇫🇷", name: "France" },
    { flag: "🇮🇳", name: "India" },
    { flag: "🇳🇱", name: "Netherlands" },
    { flag: "🇸🇪", name: "Sweden" },
    { flag: "🇩🇰", name: "Denmark" },
    { flag: "🇳🇴", name: "Norway" },
    { flag: "🇫🇮", name: "Finland" },
    { flag: "🇨🇭", name: "Switzerland" },
    { flag: "🇦🇹", name: "Austria" },
    { flag: "🇧🇪", name: "Belgium" },
    { flag: "🇮🇪", name: "Ireland" },
    { flag: "🇳🇿", name: "New Zealand" },
    { flag: "🇸🇬", name: "Singapore" },
    { flag: "🇮🇱", name: "Israel" },
    { flag: "🇪🇸", name: "Spain" },
    { flag: "🇮🇹", name: "Italy" },
    { flag: "🇵🇹", name: "Portugal" },
    { flag: "🇵🇱", name: "Poland" },
    { flag: "🇨🇿", name: "Czech Republic" },
    { flag: "🇷🇴", name: "Romania" },
    { flag: "🇧🇷", name: "Brazil" },
    { flag: "🇲🇽", name: "Mexico" },
    { flag: "🇦🇷", name: "Argentina" },
    { flag: "🇨🇴", name: "Colombia" },
    { flag: "🇨🇱", name: "Chile" },
    { flag: "🇯🇵", name: "Japan" },
    { flag: "🇰🇷", name: "South Korea" },
    { flag: "🇨🇳", name: "China" },
    { flag: "🇹🇼", name: "Taiwan" },
    { flag: "🇭🇰", name: "Hong Kong" },
    { flag: "🇦🇪", name: "United Arab Emirates" },
    { flag: "🇸🇦", name: "Saudi Arabia" },
    { flag: "🇿🇦", name: "South Africa" },
    { flag: "🇳🇬", name: "Nigeria" },
    { flag: "🇰🇪", name: "Kenya" },
    { flag: "🌐", name: "Remote" },
    { flag: "🌍", name: "Worldwide" },
].map(({ flag, name }) => ({ label: `${flag}  ${name}`, value: name }));

const PROFILE_INDUSTRY_OPTIONS = [
    { value: "Construction", label: "Construction" },
    { value: "Information Technology & Services", label: "Information Technology & Services" },
    { value: "Marketing & Advertisement", label: "Marketing & Advertisement" },
    { value: "Real Estate", label: "Real Estate" },
    { value: "Health, Wellness & Fitness", label: "Health, Wellness & Fitness" },
    { value: "Management Consulting", label: "Management Consulting" },
    { value: "Computer Software", label: "Computer Software" },
    { value: "Internet", label: "Internet" },
    { value: "Retail", label: "Retail" },
    { value: "Financial Services", label: "Financial Services" },
    { value: "Consumer Practices", label: "Consumer Practices" },
    { value: "Hospital & Healthcare", label: "Hospital & Healthcare" },
    { value: "Automotive", label: "Automotive" },
    { value: "Restaurants", label: "Restaurants" },
    { value: "Education Management", label: "Education Management" },
    { value: "Food & Beverages", label: "Food & Beverages" },
    { value: "Design", label: "Design" },
    { value: "Hospitality", label: "Hospitality" },
    { value: "Accounting", label: "Accounting" },
    { value: "Events Services", label: "Events Services" }
];

const PROFILE_DEPARTMENT_VALUE_MAP = {
    'C-Suite / Executive': 'c_suite',
    'Product': 'product_management',
    'Engineering / Technical': 'master_engineering_technical',
    'IT & Security': 'master_information_technology',
    'Design': 'design',
    'Education': 'education',
    'Finance': 'master_finance',
    'Human Resources': 'master_human_resources',
    'Legal': 'master_legal',
    'Marketing': 'master_marketing',
    'Medical & Health': 'medical_health',
    'Operations': 'master_operations',
    'Sales': 'master_sales',
    'Consulting': 'consulting'
};

const PROFILE_DEPARTMENT_OPTIONS = Object.keys(PROFILE_DEPARTMENT_VALUE_MAP).map((label) => ({
    label,
    value: label
}));

// user.preferredDepartment stores both the label and its TheirStack code
// ("IT & Security" + "master_information_technology"). The codes are backend
// plumbing (department expansion, TheirStack fetch) — never show them as
// chips/options; re-attach them on save.
const PROFILE_DEPARTMENT_CODES = new Set(Object.values(PROFILE_DEPARTMENT_VALUE_MAP));
const stripDepartmentCodes = (arr) =>
    (Array.isArray(arr) ? arr : []).filter((d) => !PROFILE_DEPARTMENT_CODES.has(d));
const withDepartmentCodes = (arr) => {
    const labels = stripDepartmentCodes(arr);
    return [...new Set([...labels, ...labels.map((l) => PROFILE_DEPARTMENT_VALUE_MAP[l]).filter(Boolean)])];
};

const WIZARD_STEPS = [
    { id: 1, label: 'Welcome' },
    { id: 2, label: 'Role & skills' },
    { id: 3, label: 'Job preferences' },
];

const QUICK_ROLES = [
    'Software Engineer', 'Senior Software Engineer', 'Product Manager', 'Data Scientist',
    'UX Designer', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer',
    'DevOps Engineer', 'Data Analyst', 'Engineering Manager', 'Marketing Manager',
];

const QUICK_SKILLS = [
    'JavaScript', 'TypeScript', 'React', 'Python', 'SQL', 'Node.js',
    'AWS', 'Docker', 'Git', 'PostgreSQL', 'REST APIs', 'Machine Learning',
    'Figma', 'Agile', 'GraphQL', 'Kubernetes', 'Product Strategy', 'Data Analysis',
];

const EXP_LEVELS = [
    { label: '0–2 yrs', value: 'entry' },
    { label: '3–5 yrs', value: 'mid' },
    { label: '6–8 yrs', value: 'senior' },
    { label: '9+ yrs', value: 'lead' },
];

const OnboardingModal = ({ handleCloseModal, isOpen, allowRetake = false }) => {
    const dispatch = useDispatch();
    const firstLogin = useSelector((state) => state.auth.firstLogin);
    const user = useSelector((state) => state.auth.user);
    const resumeData = useSelector((state) => state.resume?.resumeData);
    const resumeLoading = useSelector((state) => state.resume?.loading);

    const [sessionId, setSessionId] = useState(null);
    const [sessionError, setSessionError] = useState(null);
    const [wizardStep, setWizardStep] = useState(1);
    const [isFeatureTour, setIsFeatureTour] = useState(false);
    const [prefilledProfileData, setPrefilledProfileData] = useState(null);
    const [, setProfileConfidence] = useState({});
    const [isExtractingProfile, setIsExtractingProfile] = useState(false);
    const [hasTrackedProfileEdit, setHasTrackedProfileEdit] = useState(false);
    const [, setMissingFieldPrompts] = useState([]);
    const [cvStatus, setCvStatus] = useState('idle');
    const [lastAiRunAt, setLastAiRunAt] = useState(null);
    const [showCvSuccessToast, setShowCvSuccessToast] = useState(false);
    // Product walkthrough shown once onboarding finishes, before the modal closes.
    const [showTutorial, setShowTutorial] = useState(false);
    const [isStartingWizard, setIsStartingWizard] = useState(false);
    const [wizardStartAtMs, setWizardStartAtMs] = useState(null);
    const [applyCvExperienceEducation, setApplyCvExperienceEducation] = useState(false);
    const [isApplyingCvToProfile, setIsApplyingCvToProfile] = useState(false);
    const [roleInput, setRoleInput] = useState('');
    const [skillInput, setSkillInput] = useState('');
    const [profileDraft, setProfileDraft] = useState({
        desiredRole: '',
        desiredRoles: [],
        experienceLevel: '',
        locationPreference: '',
        skills: [],
        industryPreference: '',
        preferredIndustries: [],
        preferredDepartments: [],
        remotePreference: '',
        salaryExpectation: '',
        careerIntent: '',
        networkingPreference: ''
    });

    const isMandatoryOnboarding = firstLogin && !allowRetake;

    const trackOnboardingEvent = useCallback(async (eventName, eventData = {}) => {
        try {
            await api.post('/api/analytics/track', {
                event: eventName,
                context: 'onboarding',
                data: eventData
            });
        } catch {
            // non-blocking analytics
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setWizardStep(1);
        setIsFeatureTour(false);
        setSessionError(null);
        setPrefilledProfileData(null);
        setProfileConfidence({});
        setIsExtractingProfile(false);
        setHasTrackedProfileEdit(false);
        setMissingFieldPrompts([]);
        setCvStatus('idle');
        setLastAiRunAt(null);
        setShowCvSuccessToast(false);
        setIsStartingWizard(false);
        setWizardStartAtMs(null);
        setApplyCvExperienceEducation(false);
        setIsApplyingCvToProfile(false);
        const savedRoles = Array.isArray(user?.jobTitle) ? user.jobTitle : (user?.jobTitle ? [user.jobTitle] : []);
        const expYearsToLevel = (y) => {
            if (!y) return '';
            if (y <= 2) return 'entry';
            if (y <= 5) return 'mid';
            if (y <= 8) return 'senior';
            return 'lead';
        };
        setProfileDraft({
            desiredRole: savedRoles[0] || user?.signalProfile?.targetRole?.value || '',
            desiredRoles: savedRoles.length
                ? savedRoles
                : Array.isArray(user?.signalProfile?.targetRoles)
                    ? user.signalProfile.targetRoles.map((x) => (typeof x === 'string' ? x : x?.value)).filter(Boolean)
                    : [],
            experienceLevel: expYearsToLevel(user?.experienceYears),
            locationPreference: user?.locationPreference || '',
            skills: Array.isArray(user?.skills) ? user.skills : [],
            industryPreference: '',
            preferredIndustries: Array.isArray(user?.preferredIndustry) ? user.preferredIndustry : [],
            preferredDepartments: stripDepartmentCodes(user?.preferredDepartment),
            remotePreference: user?.remotePreference || '',
            salaryExpectation: user?.salaryExpectation || '',
            careerIntent: '',
            networkingPreference: ''
        });
        trackOnboardingEvent('onboarding_form_started', { allowRetake: Boolean(allowRetake), firstLogin: Boolean(firstLogin) });
    }, [isOpen, allowRetake, firstLogin, trackOnboardingEvent]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prevOverflow; };
    }, [isOpen]);

    const cvEducation = Array.isArray(resumeData?.education) ? resumeData.education : [];
    const cvExperience = Array.isArray(resumeData?.experience) ? resumeData.experience : [];

    const handleRequestClose = () => {
        if (isMandatoryOnboarding && wizardStep >= 2) {
            setSessionError('Please finish onboarding to continue.');
            return;
        }
        handleCloseModal();
    };

    const handleProfileDraftChange = (field, value) => {
        if (wizardStep >= 2 && !hasTrackedProfileEdit) {
            trackOnboardingEvent('profile_edited', { firstEditedField: field });
            setHasTrackedProfileEdit(true);
        }
        setProfileDraft((prev) => ({ ...prev, [field]: value }));
    };

    const toStringArray = (v) => (Array.isArray(v) ? v : v ? [v] : [])
        .map((x) => String(x || '').trim())
        .filter(Boolean);

    const unique = (arr) => [...new Set(toStringArray(arr))];

    const toSelectValues = (arr) => unique(arr).map((x) => ({ label: x, value: x }));
    const fromSelectValues = (selected) => unique((selected || []).map((x) => x?.value || x?.label).filter(Boolean));

    const DEFAULT_ROLE_OPTIONS = [
        'Software Engineer', 'Senior Software Engineer', 'Staff Software Engineer', 'Principal Engineer',
        'Engineering Manager', 'Product Manager', 'Senior Product Manager', 'Product Designer',
        'UX Designer', 'UI Designer', 'UX Researcher', 'Graphic Designer', 'Data Analyst',
        'Data Scientist', 'Machine Learning Engineer', 'AI Engineer', 'Business Analyst',
        'Financial Analyst', 'Data Engineer', 'Frontend Developer', 'Backend Developer',
        'Full Stack Developer', 'Mobile Developer', 'iOS Developer', 'Android Developer',
        'DevOps Engineer', 'Site Reliability Engineer', 'Cloud Engineer', 'Solutions Architect',
        'Technical Architect', 'QA Engineer', 'Test Engineer', 'Security Engineer',
        'Cybersecurity Analyst', 'Network Engineer', 'Systems Administrator', 'Database Administrator',
        'Marketing Manager', 'Digital Marketing Manager', 'Content Marketing Manager',
        'Social Media Manager', 'SEO Specialist', 'Sales Representative', 'Account Executive',
        'Sales Manager', 'Business Development Manager', 'Customer Success Manager', 'Account Manager',
        'Project Manager', 'Program Manager', 'Scrum Master', 'Agile Coach',
        'Technical Writer', 'Content Writer', 'Copywriter', 'HR Manager', 'Recruiter',
        'Operations Manager', 'Supply Chain Manager', 'Consultant', 'Strategy Consultant',
    ];

    const DEFAULT_SKILLS = [
        'JavaScript', 'TypeScript', 'React', 'Next.js', 'Vue.js', 'Angular', 'Node.js', 'Express',
        'Python', 'Django', 'Flask', 'FastAPI', 'Java', 'Spring', 'C#', '.NET', 'Go', 'Ruby', 'Rails',
        'SQL', 'PostgreSQL', 'MySQL', 'SQLite', 'MongoDB', 'Redis', 'Elasticsearch',
        'AWS', 'GCP', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'Linux', 'CI/CD',
        'System Design', 'REST APIs', 'GraphQL', 'Microservices', 'Testing', 'Jest',
        'Cypress', 'Playwright', 'Tailwind CSS', 'HTML', 'CSS', 'Git'
    ];

    const DEFAULT_INDUSTRIES = unique([
        ...PROFILE_INDUSTRY_OPTIONS.map((x) => x?.label || x?.value),
        'SaaS', 'FinTech', 'E-commerce'
    ]);

    const DEFAULT_DEPARTMENTS = unique(PROFILE_DEPARTMENT_OPTIONS.map((x) => x?.label || x?.value));

    const resumeRoleSuggestions = unique([
        ...(Array.isArray(resumeData?.experience) ? resumeData.experience.map((e) => e?.title) : []),
        ...(Array.isArray(resumeData?.experience) ? resumeData.experience.map((e) => e?.role) : []),
        ...(Array.isArray(resumeData?.projects) ? resumeData.projects.map((p) => p?.title) : [])
    ]).slice(0, 20);

    const existingRoleSuggestions = unique([
        ...(Array.isArray(user?.jobTitle) ? user.jobTitle : user?.jobTitle ? [user.jobTitle] : []),
        ...(Array.isArray(profileDraft?.desiredRoles) ? profileDraft.desiredRoles : []),
        profileDraft?.desiredRole
    ]).slice(0, 20);

    const roleOptions = unique([
        ...DEFAULT_ROLE_OPTIONS,
        ...existingRoleSuggestions,
        ...resumeRoleSuggestions
    ]).slice(0, 120).map((x) => ({ label: x, value: x }));

    const workModeOptions = [
        { label: 'Remote', value: 'Remote' },
        { label: 'Hybrid', value: 'Hybrid' },
        { label: 'On-site', value: 'On-site' }
    ];

    // Work mode is multi-select; stored as a comma-separated string in remotePreference.
    const selectedWorkModes = String(profileDraft.remotePreference || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    const isWorkModeSelected = (value) => selectedWorkModes.includes(value);
    const toggleWorkMode = (value) => {
        const next = isWorkModeSelected(value)
            ? selectedWorkModes.filter((v) => v !== value)
            : [...selectedWorkModes, value];
        handleProfileDraftChange('remotePreference', next.join(', '));
    };


    const skillOptions = unique([
        ...DEFAULT_SKILLS,
        ...(Array.isArray(resumeData?.skills?.technical) ? resumeData.skills.technical : []),
        ...(Array.isArray(resumeData?.skills?.soft) ? resumeData.skills.soft : []),
        ...(Array.isArray(user?.skills) ? user.skills : user?.skills ? [user.skills] : []),
        ...(Array.isArray(profileDraft?.skills) ? profileDraft.skills : [])
    ]).slice(0, 140).map((x) => ({ label: x, value: x }));

    const industryOptions = unique([
        ...DEFAULT_INDUSTRIES,
        ...(Array.isArray(user?.preferredIndustry) ? user.preferredIndustry : user?.preferredIndustry ? [user.preferredIndustry] : []),
        ...(Array.isArray(profileDraft?.preferredIndustries) ? profileDraft.preferredIndustries : [])
    ]).slice(0, 120).map((x) => ({ label: x, value: x }));

    const departmentOptions = unique([
        ...DEFAULT_DEPARTMENTS,
        ...stripDepartmentCodes(Array.isArray(user?.preferredDepartment) ? user.preferredDepartment : user?.preferredDepartment ? [user.preferredDepartment] : []),
        ...stripDepartmentCodes(profileDraft?.preferredDepartments)
    ]).slice(0, 80).map((x) => ({ label: x, value: x }));

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#3F57E0' : '#E6E8EE',
            boxShadow: state.isFocused ? '0 0 0 1px #3F57E0' : 'none',
            borderRadius: '0.75rem',
            minHeight: '40px',
            ':hover': { borderColor: state.isFocused ? '#3F57E0' : '#cbd5e1' }
        }),
        multiValue: (base) => ({ ...base, backgroundColor: '#EEF1FE', borderRadius: '9999px', paddingLeft: 2 }),
        multiValueLabel: (base) => ({ ...base, color: '#3F57E0', fontWeight: 600 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    const mapProfileToTourData = (profile) => ({
        desiredRole: profile?.desiredRole || '',
        desiredRoles: Array.isArray(profile?.desiredRoles) ? profile.desiredRoles : (profile?.desiredRole ? [profile.desiredRole] : []),
        experienceLevel: profile?.experienceLevel || '',
        locationPreference: profile?.locationPreference || '',
        skills: Array.isArray(profile?.skills) ? profile.skills : [],
        industryPreference: profile?.industryPreference || '',
        preferredIndustries: Array.isArray(profile?.preferredIndustries) ? profile.preferredIndustries : [],
        preferredDepartments: Array.isArray(profile?.preferredDepartments) ? profile.preferredDepartments : [],
        remotePreference: profile?.remotePreference || '',
        salaryExpectation: profile?.salaryExpectation || ''
    });

    const inferExperienceLevelFromResume = (parsedResume = {}) => {
        const experience = Array.isArray(parsedResume?.experience) ? parsedResume.experience : [];
        const titleText = experience
            .map((item) => `${item?.title || ''} ${item?.role || ''}`)
            .join(' ')
            .toLowerCase();

        if (/principal|staff|lead|head|director|vp|chief|manager/.test(titleText)) return 'lead';
        if (/senior|sr\.?/.test(titleText)) return 'senior';
        if (experience.length >= 2) return 'mid';
        return 'entry';
    };

    const buildProfileFromResume = (parsedResume = {}) => {
        const experience = Array.isArray(parsedResume?.experience) ? parsedResume.experience : [];
        const projects = Array.isArray(parsedResume?.projects) ? parsedResume.projects : [];
        const collectResumeSkills = (skills = {}) => {
            if (Array.isArray(skills)) return skills;
            if (!skills || typeof skills !== 'object') return [];
            return Object.values(skills)
                .flatMap((value) => Array.isArray(value) ? value : value ? [value] : [])
                .map((value) => String(value || '').trim())
                .filter(Boolean);
        };
        const allResumeSkills = collectResumeSkills(parsedResume?.skills);
        const resumeText = [
            parsedResume?.summary, parsedResume?.professional_summary, parsedResume?.objective,
            ...experience.flatMap((item) => [item?.title, item?.role, item?.company, item?.description, item?.summary]),
            ...projects.flatMap((item) => [item?.title, item?.description, item?.summary]),
            ...allResumeSkills
        ].filter(Boolean).join(' ').toLowerCase();
        const industryPatterns = [
            [/software|developer|engineer|react|node|python|java|api|cloud|saas|database|frontend|backend|full stack|devops|ai|machine learning|data science/, 'Computer Software'],
            [/information technology|it services|cybersecurity|network|systems administrator|helpdesk|infrastructure/, 'Information Technology & Services'],
            [/fintech|finance|financial|banking|investment|payments|accounting|audit|tax|insurance/, 'Financial Services'],
            [/health|healthcare|hospital|clinical|medical|patient|pharma|biotech|wellness/, 'Hospital & Healthcare'],
            [/marketing|advertising|seo|brand|campaign|content|social media|growth marketing/, 'Marketing & Advertisement'],
            [/real estate|property|brokerage|leasing|proptech|mortgage/, 'Real Estate'],
            [/construction|contractor|building|civil|site engineer|architecture/, 'Construction'],
            [/retail|ecommerce|e-commerce|consumer|shopify|merchandising|store operations/, 'Retail'],
            [/education|teacher|curriculum|student|school|university|edtech|learning/, 'Education Management'],
            [/consulting|strategy|business transformation|advisory|client engagement/, 'Management Consulting'],
            [/internet|platform|marketplace|web app|mobile app|digital product/, 'Internet'],
            [/design|ux|ui|product design|graphic design|creative/, 'Design'],
            [/automotive|vehicle|fleet|dealership|mobility|ev\b|electric vehicle/, 'Automotive'],
            [/restaurant|food|beverage|hospitality|hotel|travel|tourism/, 'Hospitality'],
            [/manufacturing|supply chain|logistics|warehouse|operations|procurement/, 'Consumer Practices']
        ];
        const inferredIndustries = industryPatterns
            .map(([pattern, industry], index) => {
                const matches = resumeText.match(pattern) || [];
                return { industry, score: matches.length * 10 - index };
            })
            .filter((item) => item.score > 0)
            .sort((a, b) => b.score - a.score)
            .map((item) => item.industry)
            .slice(0, 3);
        const role = String(
            experience[0]?.title || experience[0]?.role || projects[0]?.title ||
            profileDraft?.desiredRole || 'Open to new opportunities'
        ).trim();
        const location = String(
            parsedResume?.personal_info?.location || parsedResume?.personalInfo?.location ||
            user?.locationPreference || profileDraft?.locationPreference || 'Remote'
        ).trim();

        return {
            ...profileDraft,
            desiredRole: role,
            desiredRoles: unique([role, ...(Array.isArray(profileDraft?.desiredRoles) ? profileDraft.desiredRoles : [])]),
            experienceLevel: profileDraft?.experienceLevel || inferExperienceLevelFromResume(parsedResume),
            locationPreference: location,
            skills: unique([...allResumeSkills, ...(Array.isArray(profileDraft?.skills) ? profileDraft.skills : [])]).slice(0, 20),
            preferredIndustries: unique([
                ...(Array.isArray(profileDraft?.preferredIndustries) ? profileDraft.preferredIndustries : []),
                ...inferredIndustries
            ]).slice(0, 3),
            preferredDepartments: Array.isArray(profileDraft?.preferredDepartments) ? profileDraft.preferredDepartments : []
        };
    };

    const mergeProfileWithFallback = (profile = {}, fallback = {}) => {
        const merged = { ...fallback, ...(profile || {}) };
        merged.desiredRole = String(profile?.desiredRole || fallback?.desiredRole || '').trim();
        merged.desiredRoles = unique([
            ...(Array.isArray(profile?.desiredRoles) ? profile.desiredRoles : []),
            ...(Array.isArray(fallback?.desiredRoles) ? fallback.desiredRoles : []),
            merged.desiredRole
        ]);
        merged.experienceLevel = String(profile?.experienceLevel || fallback?.experienceLevel || '').trim();
        merged.locationPreference = String(profile?.locationPreference || fallback?.locationPreference || '').trim();
        merged.skills = unique([
            ...(Array.isArray(profile?.skills) ? profile.skills : []),
            ...(Array.isArray(fallback?.skills) ? fallback.skills : [])
        ]);
        merged.preferredIndustries = unique([
            ...(Array.isArray(profile?.preferredIndustries) ? profile.preferredIndustries : []),
            ...(Array.isArray(fallback?.preferredIndustries) ? fallback.preferredIndustries : [])
        ]).slice(0, 3);
        merged.industryPreference = String(profile?.industryPreference || fallback?.industryPreference || merged.preferredIndustries[0] || '').trim();
        merged.preferredDepartments = unique([
            ...(Array.isArray(profile?.preferredDepartments) ? profile.preferredDepartments : []),
            ...(Array.isArray(fallback?.preferredDepartments) ? fallback.preferredDepartments : [])
        ]);
        return merged;
    };

    const handleExtractProfile = async ({ resumeOverride = null, allowWithoutRole = false, autoConfirm = false } = {}) => {
        setIsExtractingProfile(true);
        setSessionError(null);
        try {
            const requiredRole = String(profileDraft?.desiredRole || '').trim();
            const effectiveResumeData = resumeOverride || resumeData || null;
            if (!requiredRole && !allowWithoutRole) {
                setSessionError('Please add at least your target role before analysis.');
                setIsExtractingProfile(false);
                return;
            }
            setCvStatus(effectiveResumeData ? 'analyzing' : 'idle');
            const response = await api.post('/api/onboarding/profile/extract', {
                formData: profileDraft,
                resumeData: effectiveResumeData
            });
            const resumeFallbackProfile = effectiveResumeData ? buildProfileFromResume(effectiveResumeData) : null;
            const extracted = mergeProfileWithFallback(response.data?.profile || {}, resumeFallbackProfile || profileDraft);
            setProfileDraft(extracted);
            setProfileConfidence(response.data?.confidence || {});
            setMissingFieldPrompts(Array.isArray(response.data?.missingFieldPrompts) ? response.data.missingFieldPrompts : []);
            setPrefilledProfileData(mapProfileToTourData(extracted));
            trackOnboardingEvent('profile_extracted', {
                hasResume: Boolean(effectiveResumeData),
                hasConfidence: Boolean(response.data?.confidence && Object.keys(response.data.confidence).length > 0)
            });
            setCvStatus(effectiveResumeData ? 'done' : 'idle');
            setLastAiRunAt(Date.now());
            if (effectiveResumeData) {
                setShowCvSuccessToast(true);
                window.setTimeout(() => setShowCvSuccessToast(false), 2200);
            }
            if (autoConfirm) {
                await handleConfirmProfile(extracted, Boolean(effectiveResumeData));
            }
        } catch (error) {
            const effectiveResumeData = resumeOverride || resumeData || null;
            if (effectiveResumeData) {
                const fallbackProfile = buildProfileFromResume(effectiveResumeData);
                setProfileDraft(fallbackProfile);
                setProfileConfidence({});
                setMissingFieldPrompts([]);
                setPrefilledProfileData(mapProfileToTourData(fallbackProfile));
                setCvStatus('done');
                if (autoConfirm) {
                    await handleConfirmProfile(fallbackProfile, true);
                }
                return;
            }
            setSessionError(error?.response?.data?.error || 'Failed to analyze profile. Please try again.');
            setCvStatus('idle');
        } finally {
            setIsExtractingProfile(false);
        }
    };

    const handleStepContinue = () => {
        if (wizardStep === 2) {
            if (!String(profileDraft?.desiredRole || '').trim()) {
                setSessionError('Target role is required. Please select or type your target role.');
                return;
            }
            if (!Array.isArray(profileDraft?.skills) || profileDraft.skills.length < 1) {
                setSessionError('Top skills are required. Please select at least one skill.');
                return;
            }
            if (!String(profileDraft?.experienceLevel || '').trim()) {
                setSessionError('Experience level is required. Please select your years of experience.');
                return;
            }
        }
        setSessionError(null);
        setWizardStep(s => s + 1);
    };

    const handleContinueToReview = async () => {
        const role = String(profileDraft?.desiredRole || '').trim();
        const level = String(profileDraft?.experienceLevel || '').trim();
        const loc = String(profileDraft?.locationPreference || '').trim();
        if (!role) {
            setSessionError('Target role is missing. Go back to "Role & skills" and add your target role.');
            return;
        }
        if (!level) {
            setSessionError('Experience level is missing. Go back to "Role & skills" and select your experience level.');
            return;
        }
        if (!loc) {
            setSessionError('Location is required. Please select your preferred location or region.');
            return;
        }
        if (!String(profileDraft?.remotePreference || '').trim()) {
            setSessionError('Work mode is required. Please select Remote, Hybrid, or On-site.');
            return;
        }
        if (!String(profileDraft?.salaryExpectation || '').trim()) {
            setSessionError('Salary expectation is required. Please enter your expected annual salary.');
            return;
        }
        if (!Array.isArray(profileDraft?.preferredIndustries) || profileDraft.preferredIndustries.length < 1) {
            setSessionError('Preferred industries are required. Please select at least one industry.');
            return;
        }
        if (!Array.isArray(profileDraft?.preferredDepartments) || profileDraft.preferredDepartments.length < 1) {
            setSessionError('Preferred departments are required. Please select at least one department.');
            return;
        }
        setSessionError(null);
        await handleExtractProfile({ allowWithoutRole: false, autoConfirm: true });
    };

    const handleUploadCvInForm = async (event) => {
        const file = event?.target?.files?.[0];
        if (!file) return;

        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(file.type)) {
            setSessionError('Please upload a PDF or Word document (.pdf, .doc, .docx).');
            if (event?.target) event.target.value = '';
            return;
        }

        try {
            setSessionError(null);
            setCvStatus('parsing');
            const parsedResume = await dispatch(uploadResume(file)).unwrap();
            dispatch(fetchUserProfile());
            trackOnboardingEvent('cv_uploaded', {
                fileName: file?.name || null,
                fileSize: Number(file?.size || 0)
            });
            await handleExtractProfile({
                resumeOverride: parsedResume,
                allowWithoutRole: true,
                autoConfirm: false
            });
            setWizardStep(2);
        } catch (error) {
            setSessionError(typeof error === 'string' ? error : 'Failed to parse CV.');
            setCvStatus('idle');
        } finally {
            if (event?.target) event.target.value = '';
        }
    };

    const handleConfirmProfile = async (profileOverride = null, autoApplyCV = false) => {
        const mapped = mapProfileToTourData(profileOverride?.desiredRole ? profileOverride : profileDraft);
        if (!mapped?.desiredRole || !mapped?.experienceLevel || !mapped?.locationPreference) {
            setSessionError('Please complete target role, experience level, and location before continuing.');
            return;
        }
        if (!isAiProfileValid(mapped, { requireSkills: false })) {
            setSessionError('Please complete required profile fields before continuing.');
            return;
        }
        setIsStartingWizard(true);
        setWizardStartAtMs(Date.now());
        try {
            if (autoApplyCV || (applyCvExperienceEducation && (cvEducation.length > 0 || cvExperience.length > 0))) {
                setIsApplyingCvToProfile(true);
                await api.post('/api/resume/apply-parsed', { replaceExperienceEducation: true });
                setIsApplyingCvToProfile(false);
            }
            let useSessionId = sessionId;
            if (!useSessionId) {
                const startResp = await api.post('/api/onboarding/session/start', {
                    startedWithCV: Boolean(
                        (Array.isArray(resumeData?.experience) && resumeData.experience.length > 0) ||
                        (Array.isArray(resumeData?.education) && resumeData.education.length > 0)
                    ),
                    prefilledSignals: user?.signalProfile || null
                });
                useSessionId = startResp.data?.sessionId || null;
                if (useSessionId) setSessionId(useSessionId);
            }
            if (useSessionId) {
                await api.post('/api/onboarding/session/complete', {
                    sessionId: useSessionId,
                    // Persist labels + their TheirStack codes; the backend's
                    // department expansion and signal fetch key on the codes.
                    profileData: { ...mapped, preferredDepartments: withDepartmentCodes(mapped?.preferredDepartments) }
                });
            }
            trackOnboardingEvent('profile_confirmed', {
                hasDesiredRole: Boolean(mapped?.desiredRole),
                skillsCount: Array.isArray(mapped?.skills) ? mapped.skills.length : 0
            });
        } catch (error) {
            setSessionError(error?.response?.data?.error || 'Could not save profile. Please try again.');
            setIsStartingWizard(false);
            setWizardStartAtMs(null);
            setIsApplyingCvToProfile(false);
            return;
        }
        const elapsed = wizardStartAtMs ? (Date.now() - wizardStartAtMs) : 0;
        const minMs = 650;
        if (elapsed < minMs) {
            await new Promise((resolve) => setTimeout(resolve, minMs - elapsed));
        }
        setPrefilledProfileData(mapped);
        setMissingFieldPrompts([]);
        setCvStatus('idle');
        setShowCvSuccessToast(false);
        setIsFeatureTour(true);
        trackOnboardingEvent('wizard_started', { source: 'profile_review' });
        setIsStartingWizard(false);
        setWizardStartAtMs(null);
    };

    const handleCompleteNavigationOnboarding = () => {
        try {
            const hasPreferredDepartment =
                Array.isArray(profileDraft?.preferredDepartments) && profileDraft.preferredDepartments.filter(Boolean).length > 0;
            const shouldStartInitialSignalScan =
                !allowRetake &&
                !user?.initialHiringSignalScan &&
                hasPreferredDepartment;

            dispatch(clearFirstLoginFlag());
            dispatch(fetchUserProfile());
            // Initial 50 jobs come from the free ATS index, matched to the profile
            // preferences just saved (previously a JSearch preference fetch).
            dispatch(fetchATSJobsByPreference({ limit: 50 }));
            if (shouldStartInitialSignalScan) {
                dispatch(fetchNewHiringSignals());
            }
        } catch (e) {
            console.error('Post-onboarding error:', e);
        }
        // Hand straight over to the walkthrough instead of closing. The scans
        // dispatched above keep running underneath, so by the time the user
        // finishes watching there is already data waiting for them.
        setShowTutorial(true);
    };

    const handleTutorialFinished = () => {
        setShowTutorial(false);
        try {
            localStorage.setItem('hasSeenTutorial', 'true');
        } catch (e) {
            // Private browsing can block localStorage — never let that trap the
            // user inside the modal.
        }
        handleCloseModal();
    };

    const toggleRole = (role) => {
        const currentRoles = Array.isArray(profileDraft.desiredRoles) ? profileDraft.desiredRoles : [];
        const isSelected = currentRoles.includes(role) || profileDraft.desiredRole === role;
        const newRoles = isSelected ? currentRoles.filter(r => r !== role) : [...currentRoles, role];
        setProfileDraft(prev => ({ ...prev, desiredRoles: newRoles, desiredRole: newRoles[0] || '' }));
    };

    const toggleSkill = (skill) => {
        const skills = Array.isArray(profileDraft.skills) ? profileDraft.skills : [];
        if (skills.includes(skill)) {
            handleProfileDraftChange('skills', skills.filter(s => s !== skill));
        } else {
            handleProfileDraftChange('skills', [...skills, skill]);
        }
    };

    if (!isOpen) return null;

    const firstName = user?.name?.split(' ')?.[0] || 'there';

    // Feature tour phase — preserve existing behaviour
    // The walkthrough replaces the whole modal once onboarding completes — it is
    // the last thing the user sees before landing in the app.
    if (showTutorial) {
        return (
            <TutorialWizard
                onClose={handleTutorialFinished}
                onFinish={handleTutorialFinished}
            />
        );
    }

    if (isFeatureTour) {
        return (
            <div className="fixed inset-0 z-[200] bg-transparent">
                {isStartingWizard && (
                    <WorkspaceLoader
                        firstName={firstName}
                        activeLabel={isApplyingCvToProfile ? 'Applying CV to your profile' : 'Saving your profile'}
                        onSkip={() => { setIsStartingWizard(false); handleCloseModal(); }}
                    />
                )}
                <OnboardingFlow
                    onComplete={handleCompleteNavigationOnboarding}
                    userFirstName={firstName}
                    prefilledData={prefilledProfileData}
                />
            </div>
        );
    }

    const isBusy = resumeLoading || isExtractingProfile || cvStatus === 'parsing' || cvStatus === 'analyzing';

    return (
        <div className="fixed inset-0 z-[200] flex flex-col bg-white overflow-hidden" role="dialog" aria-modal="true">

            {/* Loading overlay — full-screen "Preparing your workspace" loader */}
            {isStartingWizard && (
                <WorkspaceLoader
                    firstName={firstName}
                    activeLabel={isApplyingCvToProfile ? 'Applying CV to your profile' : 'Saving your profile'}
                    onSkip={() => { setIsStartingWizard(false); handleCloseModal(); }}
                />
            )}

            {/* CV success toast */}
            {showCvSuccessToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300]">
                    <div className="rounded-[18px] bg-slate-900/90 text-white px-4 py-2.5 shadow-lg backdrop-blur flex items-center gap-2">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="text-sm font-semibold">CV parsed. Profile auto‑filled.</div>
                    </div>
                </div>
            )}

            {/* ── Top bar ── */}
            <div className="flex items-center h-14 px-5 border-b border-[#E6E8EE] flex-shrink-0">
                {/* Logo */}
                <CareerChampWordmark className="text-[18px]" />

                {/* Center label */}
                <div className="flex-1 text-center px-4">
                    <span className="text-[12.5px] text-[#64748B] font-dmsans">
                        Setting up <span className="font-semibold text-[#0F172A]">{firstName}</span>'s workspace…
                    </span>
                </div>

                {/* Close */}
                <div className="flex items-center">
                    {!isMandatoryOnboarding && (
                        <button
                            type="button"
                            onClick={handleRequestClose}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F4F5F8] hover:bg-[#E6E8EE] text-[#94A3B8] hover:text-[#334155] transition-colors"
                            aria-label="Close"
                        >
                            <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* ── Body ── */}
            <div className="flex flex-1 min-h-0 overflow-hidden">

                {/* Left step rail */}
                <div className="hidden sm:flex flex-col w-[220px] lg:w-[240px] border-r border-[#E6E8EE] bg-[#FAFBFD] py-8 px-5 flex-shrink-0 relative overflow-hidden">
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full opacity-40" style={{ background: 'radial-gradient(circle, #818CF8 0%, transparent 70%)', filter: 'blur(50px)' }} />
                        <div className="absolute -top-16 right-0 w-48 h-48 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #A5B4FC 0%, transparent 70%)', filter: 'blur(40px)' }} />
                    </div>
                    <div className="relative z-10 text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] font-dmsans mb-5 px-1">Setup steps</div>
                    <div className="relative z-10 flex flex-col gap-0">
                        {WIZARD_STEPS.map((step, idx) => {
                            const isDone = wizardStep > step.id;
                            const isCurrent = wizardStep === step.id;
                            const isLast = idx === WIZARD_STEPS.length - 1;
                            return (
                                <div key={step.id} className="relative flex items-start gap-3 pb-5">
                                    {/* connector line */}
                                    {!isLast && (
                                        <div className="absolute left-[13px] top-7 bottom-0 w-px" style={{ background: isDone ? '#3F57E0' : '#E6E8EE' }} />
                                    )}
                                    {/* bubble */}
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-all ${
                                        isDone ? 'bg-[#3F57E0] shadow-sm' :
                                        isCurrent ? 'bg-white border-2 border-[#3F57E0] shadow-sm' :
                                        'bg-white border-2 border-[#E6E8EE]'
                                    }`}>
                                        {isDone ? (
                                            <svg width="11" height="9" viewBox="0 0 12 10" fill="none" aria-hidden="true">
                                                <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ) : (
                                            <div className={`w-2 h-2 rounded-full transition-all ${isCurrent ? 'bg-[#3F57E0]' : 'bg-[#D1D5DB]'}`} />
                                        )}
                                    </div>
                                    {/* label + sub */}
                                    <div className="pt-0.5">
                                        <div className={`text-[13px] font-dmsans leading-tight ${
                                            isCurrent ? 'font-semibold text-[#0F172A]' :
                                            isDone ? 'font-medium text-[#64748B]' :
                                            'font-medium text-[#B0BAC9]'
                                        }`}>
                                            {step.label}
                                        </div>
                                        {isCurrent && (
                                            <div className="text-[10.5px] text-[#3F57E0] font-dmsans font-medium mt-0.5">In progress</div>
                                        )}
                                        {isDone && (
                                            <div className="text-[10.5px] text-[#22C55E] font-dmsans font-medium mt-0.5">Done</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Center content */}
                <div className="flex-1 overflow-y-auto relative" style={{ background: '#F8F9FF' }}>
                    {/* Background orbs — center panel only */}
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-[0.12]" style={{ background: 'radial-gradient(circle, #818CF8 0%, transparent 70%)', filter: 'blur(80px)' }} />
                        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full opacity-[0.10]" style={{ background: 'radial-gradient(circle, #6366F1 0%, transparent 70%)', filter: 'blur(100px)' }} />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #3F57E0 0%, transparent 70%)', filter: 'blur(90px)' }} />
                        <div className="absolute top-8 right-1/4 w-[280px] h-[280px] rounded-full opacity-[0.09]" style={{ background: 'radial-gradient(circle, #A5B4FC 0%, transparent 70%)', filter: 'blur(65px)' }} />
                    </div>
                    <div className="relative z-10 max-w-xl mx-auto px-5 sm:px-8 py-7">

                        {/* Step header */}
                        <div className="flex items-center justify-between mb-7">
                            <div className="flex items-center gap-3">
                                <span className="text-[11.5px] text-[#94A3B8] font-dmsans">Step {wizardStep} of {WIZARD_STEPS.length}</span>
                                <div className="w-28 h-1.5 rounded-full bg-[#E6E8EE] overflow-hidden">
                                    <div
                                        className="h-1.5 rounded-full bg-[#3F57E0] transition-all duration-300"
                                        style={{ width: `${(wizardStep / WIZARD_STEPS.length) * 100}%` }}
                                    />
                                </div>
                            </div>
                            {!isMandatoryOnboarding && (
                                <button
                                    type="button"
                                    onClick={handleCompleteNavigationOnboarding}
                                    className="text-[12px] text-[#94A3B8] hover:text-[#334155] font-dmsans transition-colors"
                                >
                                    Skip
                                </button>
                            )}
                        </div>

                        {/* Error banner */}
                        {sessionError && (
                            <div className="mb-5 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100 text-[12.5px] text-red-600 font-dmsans">{sessionError}</div>
                        )}

                        {/* CV processing status */}
                        {isBusy && (
                            <div className="mb-5 rounded-xl border border-[#E6E8EE] bg-[#FAFBFD] px-4 py-3 flex items-center gap-2.5">
                                <svg className="h-4 w-4 animate-spin text-[#3F57E0] flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                                <span className="text-[12.5px] font-medium text-[#334155] font-dmsans">
                                    {cvStatus === 'parsing' ? 'Parsing your CV…' :
                                     cvStatus === 'analyzing' ? 'Analyzing with AI…' :
                                     isExtractingProfile ? 'Analyzing your profile…' : 'Working on your CV…'}
                                </span>
                            </div>
                        )}

                        {/* ── Step 1: Welcome ── */}
                        {wizardStep === 1 && (
                            <div>
                                {/* Hero */}
                                <div className="mb-7 text-center">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ background: '#EEF1FE' }}>
                                        <span className="text-[10.5px] font-semibold text-[#3F57E0] font-dmsans uppercase tracking-wide">AI-powered job search</span>
                                    </div>
                                    <h1 className="text-[26px] font-bold text-[#0F172A] font-dmsans mb-2.5 leading-tight">
                                        Welcome, {firstName}!
                                    </h1>
                                    <p className="text-[13.5px] text-[#64748B] font-dmsans leading-relaxed max-w-sm mx-auto">
                                        Set up your profile and let Career Champ find the right opportunities — automatically.
                                    </p>
                                </div>

                                {/* Feature highlights */}
                                <div className="grid grid-cols-3 gap-3 mb-7">
                                    {[
                                        { icon: (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <circle cx="12" cy="12" r="9" stroke="#3F57E0" strokeWidth="2"/>
                                                <circle cx="12" cy="12" r="5" stroke="#3F57E0" strokeWidth="2"/>
                                                <circle cx="12" cy="12" r="2" fill="#3F57E0"/>
                                            </svg>
                                        ), label: 'Job matching', desc: 'Personalized to your role' },
                                        { icon: (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#3F57E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        ), label: 'Hiring signals', desc: 'Know when to apply' },
                                        { icon: (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M9.5 2l1.3 3.9 3.9 1.3-3.9 1.3L9.5 12.5 8.2 8.5 4.3 7.2l3.9-1.3L9.5 2z" stroke="#3F57E0" strokeWidth="1.8" strokeLinejoin="round"/>
                                                <path d="M18 14l.9 2.6 2.6.9-2.6.9L18 21l-.9-2.6-2.6-.9 2.6-.9L18 14z" stroke="#3F57E0" strokeWidth="1.6" strokeLinejoin="round"/>
                                            </svg>
                                        ), label: 'AI Assistant', desc: 'Save hours of effort' },
                                    ].map(({ icon, label, desc }) => (
                                        <div key={label} className="rounded-xl border border-[#E6E8EE] p-3.5 text-left">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: '#EEF1FE' }}>{icon}</div>
                                            <div className="text-[12px] font-semibold text-[#0F172A] font-dmsans leading-tight mb-0.5">{label}</div>
                                            <div className="text-[11px] text-[#94A3B8] font-dmsans leading-snug">{desc}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* CV upload card — primary CTA */}
                                <label className={`flex items-center gap-4 rounded-xl p-5 mb-3 cursor-pointer border-2 transition-all ${
                                    isBusy
                                        ? 'border-[#E6E8EE] bg-[#FAFBFD] cursor-not-allowed opacity-70'
                                        : 'border-[#3F57E0] bg-[#EEF1FE] hover:bg-[#E5E9FC]'
                                }`}>
                                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-sm">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#3F57E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <polyline points="14 2 14 8 20 8" stroke="#3F57E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <line x1="12" y1="18" x2="12" y2="12" stroke="#3F57E0" strokeWidth="2" strokeLinecap="round"/>
                                            <polyline points="9 15 12 12 15 15" stroke="#3F57E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-[14px] font-bold text-[#3F57E0] font-dmsans mb-0.5">
                                            {cvStatus === 'parsing' ? 'Parsing…' : cvStatus === 'analyzing' ? 'Analyzing…' : 'Upload your CV'}
                                        </div>
                                        <p className="text-[12px] text-[#5B72EB] font-dmsans leading-relaxed">
                                            AI auto-fills your profile in seconds — PDF, DOC, or DOCX
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="px-3.5 py-1.5 rounded-lg bg-[#3F57E0] text-white text-[12px] font-semibold font-dmsans shadow-sm">
                                            Choose file
                                        </div>
                                    </div>
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        className="hidden"
                                        onChange={handleUploadCvInForm}
                                        disabled={isBusy}
                                    />
                                </label>

                                <div className="flex items-center gap-3 my-4">
                                    <div className="flex-1 h-px bg-[#E6E8EE]" />
                                    <span className="text-[11px] text-[#94A3B8] font-dmsans">or</span>
                                    <div className="flex-1 h-px bg-[#E6E8EE]" />
                                </div>

                                <button
                                    type="button"
                                    onClick={() => { setSessionError(null); setWizardStep(2); }}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#E6E8EE] bg-white text-[13px] font-medium text-[#334155] font-dmsans hover:border-[#C7CEDE] hover:bg-[#F8F9FC] transition-all"
                                >
                                    Set up manually instead
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* ── Step 2: Role & skills ── */}
                        {wizardStep === 2 && (
                            <div>
                                <h1 className="text-[22px] font-bold text-[#0F172A] font-dmsans mb-1 leading-tight">
                                    What do you do, {firstName}?
                                </h1>
                                <p className="text-[13px] text-[#94A3B8] font-dmsans mb-7">Select roles and skills that match your background.</p>

                                {/* Target roles */}
                                <div className="mb-7">
                                    <div className="text-[12px] font-semibold text-[#334155] font-dmsans mb-3">Target roles <span className="text-red-500">*</span></div>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {unique([
                                            ...QUICK_ROLES,
                                            ...(profileDraft.desiredRoles || []).filter(r => !QUICK_ROLES.includes(r)),
                                        ]).map(role => {
                                            const sel = (profileDraft.desiredRoles || []).includes(role) || profileDraft.desiredRole === role;
                                            return (
                                                <button
                                                    key={role}
                                                    type="button"
                                                    onClick={() => toggleRole(role)}
                                                    disabled={isBusy}
                                                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium font-dmsans transition-colors disabled:opacity-50 ${
                                                        sel ? 'bg-[#3F57E0] text-white' : 'bg-[#F4F5F8] text-[#334155] hover:bg-[#E6E8EE]'
                                                    }`}
                                                >
                                                    {role}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <CreatableSelect
                                        options={roleOptions.filter(o => !(profileDraft.desiredRoles || []).includes(o.value) && profileDraft.desiredRole !== o.value)}
                                        value={null}
                                        inputValue={roleInput}
                                        onInputChange={(v, { action }) => { if (action !== 'set-value') setRoleInput(v); }}
                                        onChange={(selected) => { if (selected?.value) { toggleRole(selected.value); setRoleInput(''); } }}
                                        onCreateOption={(inputValue) => { const v = String(inputValue || '').trim(); if (v) { toggleRole(v); setRoleInput(''); } }}
                                        placeholder="Add more roles…"
                                        isClearable={false}
                                        isDisabled={isBusy}
                                        openMenuOnFocus
                                        openMenuOnClick
                                        maxMenuHeight={280}
                                        menuPlacement="auto"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectStyles}
                                    />
                                </div>

                                {/* Skills */}
                                <div className="mb-7">
                                    <div className="flex items-baseline gap-2 mb-3">
                                        <div className="text-[12px] font-semibold text-[#334155] font-dmsans">Top skills <span className="text-red-500">*</span></div>
                                        <div className="text-[11px] text-[#94A3B8] font-dmsans">Pick at least 5</div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {unique([
                                            ...QUICK_SKILLS,
                                            ...(profileDraft.skills || []).filter(s => !QUICK_SKILLS.includes(s)),
                                        ]).map(skill => {
                                            const sel = (profileDraft.skills || []).includes(skill);
                                            return (
                                                <button
                                                    key={skill}
                                                    type="button"
                                                    onClick={() => toggleSkill(skill)}
                                                    disabled={isBusy}
                                                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium font-dmsans transition-colors disabled:opacity-50 ${
                                                        sel ? 'bg-[#3F57E0] text-white' : 'bg-[#F4F5F8] text-[#334155] hover:bg-[#E6E8EE]'
                                                    }`}
                                                >
                                                    {skill}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <CreatableSelect
                                        options={skillOptions.filter(o => !(profileDraft.skills || []).includes(o.value))}
                                        value={null}
                                        inputValue={skillInput}
                                        onInputChange={(v, { action }) => { if (action !== 'set-value') setSkillInput(v); }}
                                        onChange={(selected) => { if (selected?.value) { toggleSkill(selected.value); setSkillInput(''); } }}
                                        onCreateOption={(inputValue) => { const v = String(inputValue || '').trim(); if (v) { toggleSkill(v); setSkillInput(''); } }}
                                        placeholder="Add more skills…"
                                        isDisabled={isBusy}
                                        openMenuOnFocus
                                        openMenuOnClick
                                        maxMenuHeight={280}
                                        menuPlacement="auto"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={selectStyles}
                                    />
                                </div>

                                {/* Experience level */}
                                <div>
                                    <div className="text-[12px] font-semibold text-[#334155] font-dmsans mb-3">Years of experience <span className="text-red-500">*</span></div>
                                    <div className="flex rounded-xl border border-[#E6E8EE] overflow-hidden">
                                        {EXP_LEVELS.map(({ label, value }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => handleProfileDraftChange('experienceLevel', value)}
                                                disabled={isBusy}
                                                className={`flex-1 py-2.5 text-[12px] font-medium font-dmsans transition-colors border-r border-[#E6E8EE] last:border-r-0 disabled:opacity-50 ${
                                                    profileDraft.experienceLevel === value
                                                        ? 'bg-[#3F57E0] text-white font-semibold'
                                                        : 'text-[#334155] hover:bg-[#F4F5F8]'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Re-run AI if CV uploaded */}
                                {lastAiRunAt && (
                                    <div className="mt-4">
                                        <button
                                            type="button"
                                            onClick={() => handleExtractProfile()}
                                            disabled={isBusy}
                                            className="text-[11.5px] font-semibold text-[#3F57E0] hover:text-[#364BC8] disabled:opacity-50 font-dmsans transition-colors"
                                        >
                                            Re-run AI analysis
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Step 3: Job preferences ── */}
                        {wizardStep === 3 && (
                            <div>
                                <h1 className="text-[22px] font-bold text-[#0F172A] font-dmsans mb-1 leading-tight">
                                    What's your ideal setup?
                                </h1>
                                <p className="text-[13px] text-[#94A3B8] font-dmsans mb-7">These help us match you to the right opportunities.</p>

                                <div className="space-y-5">
                                    {/* Location */}
                                    <div>
                                        <label className="block text-[12px] font-semibold text-[#334155] font-dmsans mb-1.5">
                                            Location <span className="text-red-500">*</span>
                                        </label>
                                        <CreatableSelect
                                            options={COUNTRY_OPTIONS}
                                            value={profileDraft.locationPreference ? (COUNTRY_OPTIONS.find(o => o.value === profileDraft.locationPreference) || { label: profileDraft.locationPreference, value: profileDraft.locationPreference }) : null}
                                            onChange={(selected) => handleProfileDraftChange('locationPreference', selected?.value || '')}
                                            onCreateOption={(inputValue) => handleProfileDraftChange('locationPreference', String(inputValue || '').trim())}
                                            placeholder="Select country or region"
                                            isClearable
                                            isDisabled={isBusy}
                                            openMenuOnFocus
                                            openMenuOnClick
                                            maxMenuHeight={240}
                                            menuPlacement="auto"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    {/* Work mode */}
                                    <div>
                                        <label className="block text-[12px] font-semibold text-[#334155] font-dmsans mb-1.5">Work mode <span className="text-red-500">*</span></label>
                                        <div className="flex gap-2">
                                            {workModeOptions.map(({ label, value }) => (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    onClick={() => toggleWorkMode(value)}
                                                    disabled={isBusy}
                                                    aria-pressed={isWorkModeSelected(value)}
                                                    className={`flex-1 py-2.5 rounded-xl text-[12px] font-medium font-dmsans border transition-colors disabled:opacity-50 ${
                                                        isWorkModeSelected(value)
                                                            ? 'border-[#3F57E0] bg-[#EEF1FE] text-[#3F57E0] font-semibold'
                                                            : 'border-[#E6E8EE] text-[#334155] hover:bg-[#F4F5F8]'
                                                    }`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Salary */}
                                    <div>
                                        <label className="block text-[12px] font-semibold text-[#334155] font-dmsans mb-1.5">Expected annual salary (USD) <span className="text-red-500">*</span></label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full border border-[#E6E8EE] rounded-xl px-3 py-2.5 text-[13px] font-dmsans focus:outline-none focus:border-[#3F57E0] focus:ring-1 focus:ring-[#3F57E0] transition-colors"
                                            placeholder="e.g. 120000 (USD / year)"
                                            value={profileDraft.salaryExpectation}
                                            onChange={(e) => handleProfileDraftChange('salaryExpectation', e.target.value)}
                                            disabled={isBusy}
                                        />
                                    </div>

                                    {/* Industries */}
                                    <div>
                                        <label className="block text-[12px] font-semibold text-[#334155] font-dmsans mb-1.5">Preferred industries <span className="text-red-500">*</span></label>
                                        <CreatableSelect
                                            isMulti
                                            options={industryOptions}
                                            value={toSelectValues(profileDraft.preferredIndustries)}
                                            onChange={(selected) => handleProfileDraftChange('preferredIndustries', fromSelectValues(selected))}
                                            placeholder="Type or select industries"
                                            components={{ DropdownIndicator: null }}
                                            isDisabled={isBusy}
                                            openMenuOnFocus
                                            openMenuOnClick
                                            maxMenuHeight={280}
                                            menuPlacement="auto"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    {/* Departments */}
                                    <div>
                                        <label className="block text-[12px] font-semibold text-[#334155] font-dmsans mb-1.5">Preferred departments <span className="text-red-500">*</span></label>
                                        <CreatableSelect
                                            isMulti
                                            options={departmentOptions}
                                            value={toSelectValues(profileDraft.preferredDepartments)}
                                            onChange={(selected) => handleProfileDraftChange('preferredDepartments', fromSelectValues(selected))}
                                            placeholder="Type or select departments"
                                            components={{ DropdownIndicator: null }}
                                            isDisabled={isBusy}
                                            openMenuOnFocus
                                            openMenuOnClick
                                            maxMenuHeight={280}
                                            menuPlacement="auto"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={selectStyles}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Footer ── */}
                        <div className="mt-9 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => { setSessionError(null); setWizardStep(s => Math.max(1, s - 1)); }}
                                disabled={wizardStep === 1 || isBusy}
                                className="px-5 py-2.5 rounded-xl text-[13px] font-medium text-[#334155] font-dmsans bg-[#F4F5F8] hover:bg-[#E6E8EE] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Back
                            </button>

                            <div className="flex items-center gap-3">
                                <span className="hidden sm:block text-[11px] text-[#94A3B8] font-dmsans select-none">⌘ ↵</span>
                                {wizardStep < WIZARD_STEPS.length ? (
                                    <button
                                        type="button"
                                        onClick={handleStepContinue}
                                        disabled={isBusy}
                                        className="px-6 py-2.5 rounded-xl text-[13px] font-semibold text-white font-dmsans disabled:opacity-50 transition-colors"
                                        style={{ background: '#3F57E0' }}
                                    >
                                        Continue
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleContinueToReview}
                                        disabled={isBusy}
                                        className="px-6 py-2.5 rounded-xl text-[13px] font-semibold text-white font-dmsans disabled:opacity-50 transition-colors"
                                        style={{ background: '#3F57E0' }}
                                    >
                                        {isExtractingProfile ? 'Setting up…' : 'Finish setup'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right panel — Live preview */}
                <div className="hidden lg:flex flex-col w-[280px] xl:w-[300px] border-l border-[#E6E8EE] bg-[#FAFBFD] py-7 px-5 flex-shrink-0 relative overflow-hidden">
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-40" style={{ background: 'radial-gradient(circle, #6366F1 0%, transparent 70%)', filter: 'blur(50px)' }} />
                        <div className="absolute -bottom-16 left-0 w-48 h-48 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #A5B4FC 0%, transparent 70%)', filter: 'blur(40px)' }} />
                    </div>
                    <div className="relative z-10 text-[10.5px] font-bold uppercase tracking-wider text-[#94A3B8] font-dmsans mb-4">Live preview</div>

                    {/* Job match card */}
                    <div className="rounded-xl border border-[#E6E8EE] bg-white p-4 mb-3 relative z-10">
                        <div className="flex items-center gap-2.5 mb-3">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#3B82F6,#6366F1)' }}>
                                <span className="text-white text-[10px] font-bold">CC</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-semibold text-[#0F172A] font-dmsans truncate">
                                    {profileDraft.desiredRole || 'Your target role'}
                                </div>
                                <div className="text-[10.5px] text-[#94A3B8] font-dmsans">Top matching company</div>
                            </div>
                            {/* Score ring */}
                            <div className="relative w-9 h-9 flex-shrink-0">
                                <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36" aria-hidden="true">
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E6E8EE" strokeWidth="3"/>
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#3F57E0" strokeWidth="3" strokeDasharray="88 100" strokeLinecap="round"/>
                                </svg>
                                <span className="absolute inset-0 flex items-center justify-center text-[9.5px] font-bold text-[#CBD5E1]">—</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {(profileDraft.skills.length > 0 ? profileDraft.skills.slice(0, 4) : ['Skill 1', 'Skill 2', 'Skill 3']).map(skill => (
                                <span key={skill} className="px-2 py-0.5 rounded-md text-[9.5px] text-[#3F57E0] font-semibold font-dmsans" style={{ background: '#EEF1FE' }}>{skill}</span>
                            ))}
                        </div>
                    </div>

                    {/* AI insight card */}
                    <div className="rounded-xl border border-[#E6E8EE] bg-white p-4 mb-4 relative z-10">
                        <div className="flex items-center gap-1.5 mb-2">
                            <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: '#EEF1FE' }}>
                                <span className="text-[8px] font-bold text-[#3F57E0]">AI</span>
                            </div>
                            <span className="text-[11px] font-semibold text-[#0F172A] font-dmsans">AI insight</span>
                        </div>
                        <p className="text-[11px] text-[#64748B] leading-relaxed font-dmsans">
                            {profileDraft.desiredRole
                                ? `We'll surface ${profileDraft.desiredRole} roles matching your profile and send hiring signals when companies start hiring.`
                                : 'Complete your profile to see personalized job matches and AI-powered hiring signals.'}
                        </p>
                    </div>

                    {wizardStep < WIZARD_STEPS.length && (
                        <div className="rounded-xl border border-[#E6E8EE] bg-white p-3.5 flex flex-col items-center text-center gap-1.5 relative z-10">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#EEF1FE' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M5 12h14M12 5l7 7-7 7" stroke="#3F57E0" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="text-[9.5px] font-bold uppercase tracking-wider text-[#94A3B8] font-dmsans">Next up</div>
                            <div className="text-[11.5px] font-semibold text-[#0F172A] font-dmsans">{WIZARD_STEPS[wizardStep]?.label}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingModal;
